/**
 * Provisioning Service Selector
 *
 * Allows users to select which services to enable during router provisioning.
 * This component displays checkboxes for optional features:
 * - RADIUS Authentication
 * - SNMP Monitoring
 * - Custom Address List Sync
 * - Firewall Template Sync
 *
 * WireGuard is always enabled for remote routers and cannot be disabled.
 */

import { useState, useEffect } from 'react';
import {
    FaCheck, FaWifi, FaRadiation, FaShieldAlt, FaNetworkWired, FaLayerGroup
} from 'react-icons/fa';
import './ProvisioningServiceSelector.scss';

const ProvisioningServiceSelector = ({ onOptionsChange, initialOptions = {} }) => {
    const [options, setOptions] = useState({
        enableWireGuard: true,          // Always enabled
        enableRadiusAuth: initialOptions.enableRadiusAuth ?? false,
        enableSnmpMonitoring: initialOptions.enableSnmpMonitoring ?? false,
        enableAddressListSync: initialOptions.enableAddressListSync ?? false,
        enableFirewallTemplateSync: initialOptions.enableFirewallTemplateSync ?? false
    });

    useEffect(() => {
        onOptionsChange?.(options);
    }, [options]);

    const handleToggle = (key) => {
        if (key === 'enableWireGuard') return; // Cannot disable WireGuard
        setOptions(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const services = [
        {
            key: 'enableWireGuard',
            icon: <FaWifi />,
            title: 'WireGuard VPN Tunnel',
            description: 'Secure tunnel for remote router management (always enabled)',
            disabled: true,
            enabled: true,
            details: 'Required for secure communication with routers over untrusted networks'
        },
        {
            key: 'enableRadiusAuth',
            icon: <FaRadiation />,
            title: 'RADIUS Authentication',
            description: 'Enable user authentication on the router via RADIUS server',
            details: 'Allows centralized user management and authentication policies'
        },
        {
            key: 'enableSnmpMonitoring',
            icon: <FaShieldAlt />,
            title: 'SNMP v3 Monitoring',
            description: 'Enable SNMP for continuous router monitoring and health tracking',
            details: 'Required for CPU, memory, disk, and network performance monitoring'
        },
        {
            key: 'enableAddressListSync',
            icon: <FaNetworkWired />,
            title: 'Custom Address List Sync',
            description: 'Synchronize custom IP/DNS address lists to this router',
            details: 'Enables deployment of organization-specific address lists and firewall rules'
        },
        {
            key: 'enableFirewallTemplateSync',
            icon: <FaLayerGroup />,
            title: 'Firewall Template Sync',
            description: 'Apply standard firewall rules template to this router',
            details: 'Deploys default security rules and access control policies'
        }
    ];

    const selectedCount = Object.values(options).filter(v => v).length;
    const totalServices = services.length;

    return (
        <div className="provisioning-service-selector">
            <div className="selector-header">
                <h3>Select Provisioning Services</h3>
                <p>Choose which services to enable during router provisioning</p>
                <div className="selector-stats">
                    <span className="stat">
                        {selectedCount} of {totalServices} services selected
                    </span>
                </div>
            </div>

            <div className="services-list">
                {services.map(service => (
                    <div
                        key={service.key}
                        className={`service-card ${options[service.key] ? 'enabled' : ''} ${service.disabled ? 'disabled' : ''}`}
                        onClick={() => !service.disabled && handleToggle(service.key)}
                    >
                        <div className="card-header">
                            <div className="icon-wrapper">
                                <div className="icon">{service.icon}</div>
                                {options[service.key] && (
                                    <div className="check-badge">
                                        <FaCheck />
                                    </div>
                                )}
                            </div>
                            <div className="title-section">
                                <h4>{service.title}</h4>
                                <p className="description">{service.description}</p>
                            </div>
                            {!service.disabled && (
                                <input
                                    type="checkbox"
                                    className="service-checkbox"
                                    checked={options[service.key]}
                                    onChange={() => handleToggle(service.key)}
                                    onClick={e => e.stopPropagation()}
                                />
                            )}
                        </div>
                        <div className="card-details">
                            {service.details}
                        </div>
                    </div>
                ))}
            </div>

            <div className="selector-footer">
                <div className="info-box">
                    <strong>💡 Tip:</strong> WireGuard is always enabled for remote routers to provide secure
                    out-of-band management access. Additional services can be enabled based on your requirements.
                </div>
            </div>
        </div>
    );
};

export default ProvisioningServiceSelector;
